import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) and surfaces an
 * "are-you-still-there" warning before automatically signing the user out.
 *
 * Default flow:
 *   - 9 min of inactivity → emit `warn` (UI shows the modal)
 *   - 60 sec of additional inactivity → emit `expire` (UI calls logout)
 *   - Total: 10 min from last activity to forced logout.
 *
 * The hook is purely event-driven — it doesn't poll. Activity events are
 * throttled so heavy interaction (mousemove) doesn't thrash state.
 */
export interface UseInactivityLogoutOptions {
  /** Milliseconds of inactivity before showing the warning. Default: 9 min. */
  idleMs?: number;
  /** Milliseconds the warning is shown before forcing logout. Default: 60s. */
  warnMs?: number;
  /** Callback when the warning should be displayed. */
  onWarn: () => void;
  /** Callback when the session must be terminated. */
  onExpire: () => void;
}

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;
// Throttle so mousemove (which can fire 60+ times per second) doesn't reset
// the timer in a tight loop. 1s is more than enough resolution.
const ACTIVITY_THROTTLE_MS = 1000;

export function useInactivityLogout({
  idleMs = 9 * 60 * 1000,
  warnMs = 60 * 1000,
  onWarn,
  onExpire,
}: UseInactivityLogoutOptions) {
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
    idleTimerRef.current = null;
    expireTimerRef.current = null;
  }, []);

  const startWarn = useCallback(() => {
    warnedRef.current = true;
    onWarn();
    expireTimerRef.current = setTimeout(() => {
      onExpire();
    }, warnMs);
  }, [onWarn, onExpire, warnMs]);

  const scheduleIdle = useCallback(() => {
    clearTimers();
    warnedRef.current = false;
    idleTimerRef.current = setTimeout(startWarn, idleMs);
  }, [clearTimers, idleMs, startWarn]);

  /** Public reset (called by the modal's "I'm here" button). */
  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    scheduleIdle();
  }, [scheduleIdle]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      return;
    }

    scheduleIdle();

    const handleActivity = () => {
      // Once the warning is showing, ignore activity — the user must explicitly
      // click "I'm still here" to dismiss. Otherwise an accidental mouse twitch
      // would silently cancel the warning the user never saw.
      if (warnedRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      scheduleIdle();
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, handleActivity, { passive: true });
    }

    return () => {
      clearTimers();
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, handleActivity);
      }
    };
  }, [isAuthenticated, scheduleIdle, clearTimers]);

  return { reset };
}

/**
 * Convenience companion hook that exposes a 1-second countdown for the
 * warning modal. Starts when `active=true`, stops when `active=false`.
 */
export function useCountdown(seconds: number, active: boolean) {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    if (!active) {
      setRemaining(seconds);
      return;
    }
    setRemaining(seconds);
    const interval = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [active, seconds]);

  return remaining;
}
